import { Component } from "@theme/component";
import { debounce, isClickedOutside, onAnimationEnd } from "@theme/utilities";

export class DialogOpenEvent extends CustomEvent {
  static eventName = "dialog:open";
  constructor() {
    super(DialogOpenEvent.eventName);
  }
}

export class DialogCloseEvent extends CustomEvent {
  static eventName = "dialog:close";
  constructor() {
    super(DialogCloseEvent.eventName);
  }
}

/**
 * Shows or hides the ring on the focused element. The browser only redraws it on a fresh focus, so the element
 * is blurred first.
 *
 * @param {boolean} ring - Whether the focused element should show its focus ring.
 */
export function showFocusRing(ring) {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body || active.matches(":focus-visible") === ring) {
    return;
  }

  active.blur();
  active.focus({ preventScroll: true, focusVisible: ring });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ["dialog"];

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) {
      return;
    }

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #pendingShow = false;
  /** Whether focus returns with a ring after Escape: the dialog was opened from the keyboard, or Tab used in it. */
  #ringOnEscape = false;
  #closingOnEscape = false;

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    this.#pendingShow = false;

    const { dialog } = this.refs;

    if (!dialog.open) {
      return;
    }

    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = "none";

    // Force a reflow
    dialog.getBoundingClientRect();

    // Now add the closing class and restore animation
    dialog.classList.add("dialog-closing");
    dialog.style.animation = "";

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });

    dialog.close();
    dialog.classList.remove("dialog-closing");

    // The browser hands focus back with a ring after any key press. Escape leaves none for a visitor who clicked,
    // and keeps it for one moving around with the keyboard, who needs to see where focus landed.
    if (this.#closingOnEscape) {
      this.#closingOnEscape = false;
      showFocusRing(this.#ringOnEscape);
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener("resize", this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener("resize", this.#handleResize);
      this.#handleResize.cancel();
    }
  }

  /** Allow subclasses to recheck eligibility immediately before opening and locking scroll. */
  canOpenDialog() {
    return true;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) {
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    this.#pendingShow = true;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (!this.#pendingShow) {
        return;
      }
      this.#pendingShow = false;
      if (!this.canOpenDialog()) {
        return;
      }

      document.body.style.width = "100%";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;

      this.#ringOnEscape = document.activeElement?.matches(":focus-visible") ?? false;
      this.#closingOnEscape = false;
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener("click", this.#handleClick);
      this.addEventListener("keydown", this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (!this.hasAttribute("disable-backdrop-close") && isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key === "Tab") {
      this.#ringOnEscape = true;
    }
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    this.#closingOnEscape = true;
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute("dialog-active-min-width"));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute("dialog-active-max-width"));
  }
}

if (!customElements.get("dialog-component")) {
  customElements.define("dialog-component", DialogComponent);
}

const SCROLL_LOCK = "scroll-lock";

document.addEventListener(
  "toggle",
  event => {
    if (!(event.target instanceof HTMLDetailsElement && event.target.hasAttribute(SCROLL_LOCK))) {
      return;
    }

    const { open } = event.target;
    if (open) {
      document.documentElement.setAttribute(SCROLL_LOCK, "");
    } else {
      document.documentElement.removeAttribute(SCROLL_LOCK);
    }
  },
  { capture: true },
);
